import LazyLoadImage from './lazyLoadImage'
import PropTypes from 'prop-types'
import React, {PureComponent} from 'react'
import identity from 'lodash/identity'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import {css} from 'glamor'
import {qs} from '../shared/utils'
import {replaceParameters, replaceSelector} from '../shared/params'

const formatProduct = (id, color, view) => {
  color = color
    .replace(/[\/\\\.:\*\?<>|'&]/g, '_')
    .trim()
    .toLowerCase()
  view = `${view[0].toUpperCase()}${view.slice(1).toLowerCase()}`

  return `${id}/${color}_${view}`
}

const safeColor = (color) => color && color.match(/[\d|a-f]{6}/i)[0]

// Many instances in the API/DB where the data returned/stored does not match
// anything in the Imageserver. Try to capture the necessary sanitizations
// here.
const formatDesignSelector = (group, selector, negative) => {
  if (group === 'BLANK') {
    return ''
  }

  const replacements = {
    APPLYLC: (selector) => selector.replace('Alphabet', 'alphabet'),
    COLLECTION: (selector) => selector.replace('lc_1', 'LC_1'),
    XTPLEMB43: (selector) => selector.replace('emb', 'EMB'),
    XTPLEMB47: (selector) => selector.replace('emb', 'EMB'),
    XTPLEMB49: (selector) => selector.replace('emb', 'EMB'),
    XTPLEMBK12: (selector) => selector.replace('lc_1', 'LC_1'),
    MASCOT: (selector, i, items) =>
      i === items.length - 1 ? selector.toLowerCase() : selector
  }

  group = group.trim()
  selector = selector
    .trim()
    .replace(/\\+/g, '/')
    .split('/')
    .map(replacements[group] || identity)
    .join('/')

  if (negative) {
    selector += '_n'
  }

  return `${group}/${selector}`
}

export const getImageQuery = ({
  activity,
  bgColor,
  colors,
  design,
  height,
  personalization,
  productId,
  productColor,
  maskColor,
  negative,
  store,
  view = 'Front'
}) => {
  const context = {
    ...personalization,
    activity,
    design,
    store,
    productColor
  }

  const param = [
    design.Parameter1,
    design.Parameter2,
    design.Parameter3,
    design.Parameter4,
    design.Parameter5,
    design.Parameter6
  ]
    .map((param) => replaceParameters(param, context))
    .join('|')

  const query = omitBy(
    {
      p: formatProduct(productId, productColor, view),
      d: formatDesignSelector(
        design.DesignGroup,
        replaceSelector(design, context),
        negative
      ),
      mask: maskColor ? formatProduct(productId, maskColor, view) : null,
      param,
      bg: bgColor ? `${safeColor(bgColor)}ff` : null,
      color: colors.map((color) => `${color}ff`).join('|'),
      h: height,
      output: 'png'
    },
    isNil
  )
  return qs(query)
}

export default class ProductImageV2 extends PureComponent {
  render() {
    /* eslint-disable no-unused-vars */
    const {
      activity,
      bgColor,
      colors,
      design,
      height,
      imageHost,
      personalization,
      productId,
      productColor,
      maskColor,
      negative,
      responsive,
      store,
      style,
      type,
      view,
      onError,
      onLoad,
      lazy,
      ...rest
    } = this.props
    /* eslint-enable no-unused-vars */

    if (!(productId && design && store)) {
      return null
    }

    if (type === 'design' && design.DesignName === 'Blank') {
      return (
        <span
          {...css(
            {display: 'inline-block', background: `#${safeColor(bgColor)}`},
            style
          )}
        >
          <img {...css({visibility: 'hidden'})} />
        </span>
      )
    }

    return (
      <LazyLoadImage
        event={{
          action: 'impression',
          product: productId,
          design: design.DesignID
        }}
        force={!lazy}
        onError={onError}
        onLoad={onLoad}
        responsive={responsive}
        src={`${imageHost}/${type}?${getImageQuery(this.props)}`}
        style={style}
        {...rest}
      />
    )
  }
}

ProductImageV2.propTypes = {
  activity: PropTypes.string,
  bgColor: PropTypes.string,
  colors: PropTypes.array,
  design: PropTypes.object.isRequired,
  designParams: PropTypes.object,
  height: PropTypes.number,
  imageHost: PropTypes.string,
  onError: PropTypes.func,
  onLoad: PropTypes.func,
  personalization: PropTypes.object,
  productId: PropTypes.number.isRequired,
  productColor: PropTypes.string,
  maskColor: PropTypes.string,
  negative: PropTypes.bool,
  responsive: PropTypes.bool,
  store: PropTypes.object.isRequired,
  style: PropTypes.object,
  type: PropTypes.string,
  view: PropTypes.string,
  lazy: PropTypes.bool
}

ProductImageV2.defaultProps = {
  type: 'pi',
  view: 'Front',
  negative: false,
  lazy: true
}
